import React, { useState, useEffect, useContext, useCallback, useMemo, useRef } from "react";
import { AuthContext } from "../context/AuthContext";
import { getFleetDriver } from "../services/fleetDriverService";
import {
  API_REQUEST_HEADERS_BASEURL,
  assertFleetOperationsAllowed,
  API_FleetDriver_ID_post_cards,
  API_FleetDriver_ID_get_onecard_OT,
  API_FleetDriver_ID_finalize_OT,
  API_FleetDriver_ID_patch_status_OT,
  API_FleetOrderEmailAlias,
  API_FleetTransportOrderDraft,
  API_FleetTransportOrderDraft_reprocess,
  API_FleetTransportOrderDraft_review,
  API_FleetTransportOrderDrafts,
  API_FleetTransportOrderDrafts_extract,
  API_FleetTransportOrderDrafts_processReady,
  API_FleetTransportOrderDrafts_settings,
} from "../utils/Constants";
import styled from "@emotion/styled";
import { Alert, Button, Checkbox, Collapse, List, Modal, Radio, Select, Space, Tabs, Tag, Typography, Input, Upload, Empty } from "antd";
import { DragDropContext, Droppable } from "react-beautiful-dnd";
import { useTranslation } from "react-i18next";
import Divider from "../components/Divider";
import KanbanCard from "../components/assignmentBoard/KanbanCard";
import TransportOrderFilter from "../components/transportOrderFilter/TransportOrderFilter";
import BroadcastComponent from "../components/broadcast/Broadcast";
import { useGlobalToast } from "../context/ToastProvider";
import { fleetErrorMessage } from "../utils/fleetFeedback";
import {
  CopyOutlined,
  DeleteOutlined,
  EyeOutlined,
  FileOutlined,
  FilePdfOutlined,
  FileTextOutlined,
  HistoryOutlined,
  InboxOutlined,
  MailOutlined,
  MessageOutlined,
  PictureOutlined,
  PlusOutlined,
  PlayCircleOutlined,
  ReloadOutlined,
  SearchOutlined,
  SettingOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import OrderForm from "../components/orderForm/orderForm";
import { db, storage } from "../firebase";
import {
  collection,
  deleteField,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  updateDoc,
  where,
  writeBatch,
} from "firebase/firestore";
import { getDownloadURL, ref as storageRef, uploadBytes } from "firebase/storage";
import { DriverContext } from "../context/DriverContext";
import ContextToolbar from "../components/contextToolbar/ContextToolbar";
import { matchesOrderSearch } from "../utils/orderSearch";
import { activateAssignedOrders } from "../utils/activateAssignedOrders";
import { useChat } from "../context/ChatContext";
import { useSearchParams } from "react-router-dom";
import "./AssignmentKanban.css";

const TaskColumnStyles = styled("div")({
  display: "flex",
  flexDirection: "row",
  justifyContent: "space-between",
  width: "100%",
  gap: "5px",
});

const TaskList = styled("div")(({ columnId, dimmed }) => ({
  minHeight: "95vh",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  background: "#d7dce8",
  width: ["IP", "CL"].includes(columnId) ? "35%" : "20.5%",
  borderRadius: "5px",
  padding: "15px",
  boxSizing: "border-box",
  transition: "opacity 120ms ease-in-out",
  opacity: dimmed ? 0.55 : 1,
}));

const Title = styled("span")({
  fontWeight: "bold",
  color: "#333333",
  fontSize: 16,
  marginBottom: "1.5px",
  height: "25px",
});

const ColumnActionButton = styled(Button)({
  height: 28,
  padding: "0 10px",
  borderRadius: 6,
  fontSize: 12,
  fontWeight: 500,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
});

const ORDER_IMPORT_WARNING_TRANSLATION_KEYS = {
  MISSING_TRANSPORT_ORDER_REFERENCE: "orderImport.warnings.missingTransportOrderReference",
  "Missing transport order reference": "orderImport.warnings.missingTransportOrderReference",
  INSUFFICIENT_ROUTE_POINTS: "orderImport.warnings.insufficientRoutePoints",
  "Less than two route points were detected": "orderImport.warnings.insufficientRoutePoints",
  ROUTE_POINTS_NEED_MAP_CONFIRMATION: "orderImport.warnings.routePointsNeedMapConfirmation",
  "Some route points need map confirmation": "orderImport.warnings.routePointsNeedMapConfirmation",
  MISSING_GOODS_DESCRIPTION: "orderImport.warnings.missingGoodsDescription",
  "Goods description is missing": "orderImport.warnings.missingGoodsDescription",
};

const CREATE_ORDER_MODAL_Z_INDEX = 1200;
const CREATE_ORDER_SECONDARY_MODAL_Z_INDEX = 1600;

const localizeOrderImportWarnings = (warnings, t) =>
  (Array.isArray(warnings) ? warnings : [])
    .map((warning) => {
      const value = typeof warning === "string" ? warning : warning?.code || warning?.message || "";
      const translationKey = ORDER_IMPORT_WARNING_TRANSLATION_KEYS[value];
      return translationKey ? t(translationKey) : t("orderImport.reviewDetectedFields");
    })
    .filter(Boolean);

const normalizePlaceRuleText = (value) => String(value || "").trim();
const normalizePlaceRuleName = (value) => normalizePlaceRuleText(value)
  .normalize("NFD")
  .replace(/[\u0300-\u036f]/g, "")
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, " ")
  .trim();
const placeRuleKey = (item = {}) => [
  normalizePlaceRuleName(item.sourceName),
  normalizePlaceRuleText(item.sourceCode).toUpperCase(),
  normalizePlaceRuleText(item.placeId),
].join("\u0000");

const collectPotentialPlaceMatchRules = (draft = {}, existingRules = []) => {
  const resolution = draft?.directoryResolution || {};
  const selections = [resolution.pickup, resolution.dropoff, ...(resolution.checkpoints || [])]
    .filter((item) => item?.manuallyConfirmed && item?.extracted?.name && item?.selected?.id)
    .map((item) => ({
      sourceName: normalizePlaceRuleText(item.extracted.name),
      sourceCode: normalizePlaceRuleText(item.extracted.code).toUpperCase(),
      placeId: normalizePlaceRuleText(item.selected.placeId || item.selected.id),
      placeName: normalizePlaceRuleText(item.selected.name),
    }))
    .filter((item) => item.sourceName && item.placeId && item.placeName);
  const existing = new Set((Array.isArray(existingRules) ? existingRules : []).map(placeRuleKey));
  return selections.filter((item, index, all) => (
    !existing.has(placeRuleKey(item))
    && all.findIndex((candidate) => placeRuleKey(candidate) === placeRuleKey(item)) === index
  ));
};

const AssignmentKanban = () => {
  const { getFleetID, isLoading, fleetData } = useContext(AuthContext);
  const { driversDetails: reactiveDriversDetails, driversDetailsById } = useContext(DriverContext);
  const { openChat } = useChat();
  const [searchParams, setSearchParams] = useSearchParams();
  const fleetId = getFleetID();
  const [columns, setColumns] = useState({});
  const [bulkActivationOpen, setBulkActivationOpen] = useState(false);
  const [bulkActivationProgress, setBulkActivationProgress] = useState(null);
  const [bulkActivationResults, setBulkActivationResults] = useState(null);
  const bulkActivationRunningRef = useRef(false);
  const [checkpoints, setCheckpoints] = useState([{ latitude: 0, longitude: 0 }]);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [importedOrderDraft, setImportedOrderDraft] = useState(null);
  const [importedOrderDraftId, setImportedOrderDraftId] = useState("");
  const [orderFormKey, setOrderFormKey] = useState("create-empty");
  const [orderImportText, setOrderImportText] = useState("");
  const [orderImportFiles, setOrderImportFiles] = useState([]);
  const [orderImportMode, setOrderImportMode] = useState("EMAIL");
  const [orderEmailAlias, setOrderEmailAlias] = useState(null);
  const [orderEmailAliasLoading, setOrderEmailAliasLoading] = useState(false);
  const [isExtractingOrder, setIsExtractingOrder] = useState(false);
  const [orderDrafts, setOrderDrafts] = useState([]);
  const [orderDraftsLoading, setOrderDraftsLoading] = useState(false);
  const [orderDraftsPage, setOrderDraftsPage] = useState(1);
  const [reprocessingOrderDraftId, setReprocessingOrderDraftId] = useState("");
  const [orderImportLogs, setOrderImportLogs] = useState([]);
  const [orderImportLogsLoading, setOrderImportLogsLoading] = useState(false);
  const [isImportLogsOpen, setIsImportLogsOpen] = useState(false);
  const [selectedImportLog, setSelectedImportLog] = useState(null);
  const [orderImportReferenceFilter, setOrderImportReferenceFilter] = useState("");
  const [orderImportLogsPage, setOrderImportLogsPage] = useState(1);
  const [orderImportSpecialInstructions, setOrderImportSpecialInstructions] = useState("");
  const [draftOrderImportSpecialInstructions, setDraftOrderImportSpecialInstructions] = useState("");
  const [orderImportPlaceMatchMemory, setOrderImportPlaceMatchMemory] = useState([]);
  const [draftOrderImportPlaceMatchMemory, setDraftOrderImportPlaceMatchMemory] = useState([]);
  const [orderImportSettingsLoading, setOrderImportSettingsLoading] = useState(false);
  const [orderImportSettingsSaving, setOrderImportSettingsSaving] = useState(false);
  const [isOrderImportSettingsOpen, setIsOrderImportSettingsOpen] = useState(false);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [assignTarget, setAssignTarget] = useState(null);
  const [assignDriverId, setAssignDriverId] = useState(null);
  const [isFinalizeConfirmOpen, setIsFinalizeConfirmOpen] = useState(false);
  const [finalizeTarget, setFinalizeTarget] = useState(null);
  const [finalizeStatusLoading, setFinalizeStatusLoading] = useState(false);
  const [finalizeStatusUnavailable, setFinalizeStatusUnavailable] = useState(false);
  const [finalizeFromStatus, setFinalizeFromStatus] = useState(null);
  const [finalizeReason, setFinalizeReason] = useState("");
  const [eDocFinalizationAction, setEDocFinalizationAction] = useState("DECLARE_DELIVERED");
  const [deliveryConfirmed, setDeliveryConfirmed] = useState(false);
  const [allowedDropIds, setAllowedDropIds] = useState([]);
  const [activeOrderDetailId, setActiveOrderDetailId] = useState(null);
  const { t } = useTranslation();
  const [filters, setFilters] = useState({ query: "", selectedOrderId: "" });
  const [allTransportOrders, setAllTransportOrders] = useState([]);
  const activationErrorSignaturesRef = useRef({});
  const finalizationErrorSignaturesRef = useRef({});
  const finalizationErrorFleetRef = useRef(null);
  const hasLoadedActivationErrorsRef = useRef(false);

  const toast = useGlobalToast();
  useEffect(() => {
    if (!isFinalizeConfirmOpen || !finalizeTarget?.id || !finalizeTarget.raw?.freightDocumentId) return;
    const controller = new AbortController();
    setFinalizeStatusLoading(true); setFinalizeStatusUnavailable(false);
    const endpoint = API_FleetDriver_ID_get_onecard_OT.replace('{fleetId}', fleetId).replace('{fleetOT}', finalizeTarget.id);
    API_REQUEST_HEADERS_BASEURL.get(endpoint, { signal: controller.signal }).then(response => {
      if (controller.signal.aborted) return;
      const data = response.data?.data;
      setFinalizeStatusUnavailable(!data?.edocStatus || data.edocStatusUnavailable === true);
      setFinalizeTarget(previous => previous ? { ...previous, raw: { ...previous.raw, edocStatus: data?.edocStatus || null } } : previous);
    }).catch(() => { if (!controller.signal.aborted) setFinalizeStatusUnavailable(true); })
      .finally(() => { if (!controller.signal.aborted) setFinalizeStatusLoading(false); });
    return () => controller.abort();
  }, [isFinalizeConfirmOpen, finalizeTarget?.id, finalizeTarget?.raw?.freightDocumentId, fleetId]);
  const normalizeId = useCallback((value) => String(value || "").trim(), []);

  const getTranslatedMessage = useCallback(
    (messageKey, fallbackMessage) => {
      if (messageKey) {
        const translated = t(messageKey);
        if (translated && translated !== messageKey) {
          return translated;
        }
      }
      return fallbackMessage || t("feedback.unavailable");
    },
    [t]
  );

  const getDriverAssignmentBlock = useCallback(
    (driverId, verifiedDriver) => {
      const normalizedDriverId = normalizeId(driverId);
      if (!normalizedDriverId) {
        return {
          messageKey: "toast.assignDriverBeforeActivate",
          defaultMessage: "Select a driver before continuing.",
        };
      }

      const driver = verifiedDriver || driversDetailsById[normalizedDriverId];
      if (driver?.detailsLoaded === false) {
        return { messageKey: "toast.checkingDriver", defaultMessage: "Checking driver details. Please try again shortly." };
      }
      if (!driver) {
        return {
          messageKey: "toast.driverNotFound",
          defaultMessage: "The selected driver is not available in this fleet.",
        };
      }

      const normalizedFleetId = normalizeId(fleetId);
      const driverCurrentFleet = normalizeId(driver.currentFleet);
      if (driverCurrentFleet !== normalizedFleetId) {
        return {
          messageKey: "toast.driverInAnotherFleet",
          defaultMessage:
            "The selected driver is not currently working with this fleet. Ask the driver to switch fleet first.",
        };
      }

      return null;
    },
    [driversDetailsById, fleetId, normalizeId]
  );

  const verifyDriverAssignmentBlock = async (driverId) => {
    if (!normalizeId(driverId)) return getDriverAssignmentBlock(driverId);
    try {
      const driver = await getFleetDriver(fleetId, normalizeId(driverId));
      if (!driver) return { messageKey: "toast.driverNotFound", defaultMessage: "Driver not found." };
      return getDriverAssignmentBlock(driverId, { ...driver, detailsLoaded: true });
    } catch (error) {
      if (error?.response?.status === 404) return { messageKey: "toast.driverNotFound", defaultMessage: "Driver not found." };
      return { messageKey: "toast.driverDetailsUnavailable", defaultMessage: "Unable to verify driver details. Please try again." };
    }
  };

  const sortByDate = (field) => (a, b) => {
    const dateA = new Date(a.raw?.[field] || 0).getTime();
    const dateB = new Date(b.raw?.[field] || 0).getTime();

    return dateB - dateA;
  };

  useEffect(() => {
    setColumns({
      NA: { title: t("transportManagement.pending"), items: [] }, //PENDING
      AS: { title: t("transportManagement.assigned"), items: [] }, //ASSIGNED
      IP: { title: t("transportManagement.active"), items: [] }, //ACTIVE
      CL: { title: t("transportManagement.finalized"), items: [] }, //FINALIZED
    });
  }, [t]);

  const driverList = useMemo(
    () =>
      reactiveDriversDetails
        .filter((driver) => normalizeId(driver?.id))
        .map((driver) => {
          const block = getDriverAssignmentBlock(driver.id);
          return {
            id: driver.id,
            name: driver.name || "Driver",
            disabled: !!block,
            disabledReason: block
              ? getTranslatedMessage(block.messageKey, block.defaultMessage)
              : "",
          };
        }),
    [getDriverAssignmentBlock, getTranslatedMessage, normalizeId, reactiveDriversDetails]
  );
  const toolbarTags = useMemo(
    () => [...new Set(reactiveDriversDetails.flatMap((driver) => driver.tags || []).filter(Boolean))],
    [reactiveDriversDetails]
  );

  const removeAssignDriver = async (transportOrder) => {
    try {
      if (!fleetId || !transportOrder?.id) return false;
      await assertFleetOperationsAllowed(fleetId);
      const orderRef = doc(db, "fleets", fleetId, "transport_orders", transportOrder.id);
      await updateDoc(orderRef, {
        userId: "",
        status: "PENDING",
      });
      return true;
    } catch (error) {
      toast({ error,
        title: t("toast.errorRemovingDriver", {
          defaultValue: "There was an error removing the driver.",
        }),
        status: "warning",
      });
      console.error("Error removing driver from order:", error);
      return false;
    }
  };

  const assignDriverToTransportOrder = async (transportOrderId, userId) => {
    try {
      if (!fleetId || !transportOrderId || !userId) return false;
      await assertFleetOperationsAllowed(fleetId);
      const block = await verifyDriverAssignmentBlock(userId);
      if (block) {
        toast({
          title: getTranslatedMessage(block.messageKey, block.defaultMessage),
          status: "warning",
        });
        return false;
      }
      const orderRef = doc(db, "fleets", fleetId, "transport_orders", transportOrderId);
      await updateDoc(orderRef, {
        userId,
        status: "ASSIGNED",
        activationError: deleteField(),
      });
      return true;
    } catch (error) {
      toast({ error,
        title: getTranslatedMessage(
          error?.response?.data?.messageKey,
          t("toast.assignDriverError", {
            defaultValue: "There was an error assigning the driver.",
          })
        ),
        status: "warning",
      });
      console.error("Error assigning driver to order:", error);
      return false;
    }
  };

  const assignDriverToOrder = async () => {
    if (!assignTarget || !assignDriverId) {
      return;
    }

    const success = await assignDriverToTransportOrder(assignTarget.id, assignDriverId);
    if (success) {
      setIsAssignModalOpen(false);
      setAssignTarget(null);
      setAssignDriverId(null);
    }
  };

  const assignDriverFromCard = async (transportOrder, userId) =>
    assignDriverToTransportOrder(transportOrder?.id, userId);

  const openOrderChat = useCallback(
    (transportOrder) => {
      if (!transportOrder) return;
      const orderId = String(transportOrder.id || "").trim();
      if (!orderId) return;
      const driverId = String(transportOrder?.raw?.userId || "").trim();
      const driver = driverId ? driversDetailsById[driverId] : null;
      const driverName = driver?.name || "Driver";
      openChat({
        key: `TO-${orderId}-${driverId || "none"}-${orderId}`,
        id: driverId || orderId,
        name: `${transportOrder.ref || orderId} - ${driverName}`,
        driverStatus: driver?.isOnline ? "online" : "offline",
        type: "TO",
        currentOrder: orderId,
        transportOrderId: orderId,
        profilePic: driver?.profilePic || null,
      });
    },
    [driversDetailsById, openChat]
  );

  const requiresFinalizeReason = finalizeFromStatus === "ACTIVE";

  const updateStatus = async (transportOrder, newStatus, fromStatus, deliveredOnly = false) => {
    const fallbackKey =
      newStatus === "FINALIZED" ? "toast.errorFinalizingOrder" : "toast.errorActivatingOrder";
    const fallbackText =
      newStatus === "FINALIZED"
        ? "There was an error finalizing the order."
        : "There was an error activating the order.";
    try {
      if (!fleetId || !transportOrder?.id) {
        return false;
      }

      if (newStatus === "ACTIVE") {
        // The fleet-authorized API validates the driver's current fleet/order.
        // Operations members cannot read the private drivers document directly.
        const endpoint = API_FleetDriver_ID_patch_status_OT
          .replace("{fleetId}", fleetId)
          .replace("{transportOrderId}", transportOrder.id);
        await API_REQUEST_HEADERS_BASEURL.patch(endpoint, {
          newStatus: "ACTIVE",
          fromStatus: fromStatus || transportOrder?.raw?.status || "ASSIGNED",
        });
      } else if (newStatus === "FINALIZED") {
        const endpoint = API_FleetDriver_ID_finalize_OT
          .replace("{fleetId}", fleetId)
          .replace("{transportOrderId}", transportOrder.id);
        await API_REQUEST_HEADERS_BASEURL.post(endpoint, {
          reason: deliveredOnly ? "" : finalizeReason || "",
          eDocFinalizationAction: deliveredOnly ? "REQUIRE_DELIVERED" : eDocFinalizationAction,
          deliveryConfirmed: deliveredOnly ? false : deliveryConfirmed,
          cancelEDocOnFinalize: !deliveredOnly && eDocFinalizationAction === "CANCEL",
        });
      }

      const ok = true;
      if (ok && newStatus === "FINALIZED") {
        toast({
          title: t("toast.orderFinalized", {
            defaultValue: "Order finalized successfully.",
          }),
          status: "success",
        });
      }
      return true;
    } catch (error) {
      const messageKey = error?.response?.data?.messageKey;
      if (deliveredOnly && messageKey === "toast.transFollowDeliveryProofRequired") return "NEEDS_CONFIRMATION";
      toast({ error,
        title: t(fallbackKey, { defaultValue: fallbackText }),
        status: "warning",
      });
      console.error("Error updating order status:", error);
      return false;
    }
  };

  const updateColumnItems = useCallback(
    (transportOrders) => {
      let filtered = [...transportOrders];

      if (filters.selectedOrderId) {
        filtered = filtered.filter((order) => String(order.id) === String(filters.selectedOrderId));
      } else if (filters.query) {
        filtered = filtered.filter((order) =>
          matchesOrderSearch(order, filters.query, driversDetailsById)
        );
      }

      const sortDesc = (field) => (a, b) => (b.raw?.[field] ?? 0) - (a.raw?.[field] ?? 0);

      const groupedOrders = {
        NA: filtered
          .filter((o) => o.status === "PENDING")
          .slice()
          .sort(sortDesc("activationAt")),

        AS: filtered
          .filter((o) => o.status === "ASSIGNED")
          .slice()
          .sort(sortDesc("activationAt")),

        IP: filtered
          .filter((o) => o.status === "ACTIVE")
          .slice()
          .sort(sortDesc("activatedAt")),

        CL: filtered
          .filter((o) => o.status === "FINALIZED")
          .slice()
          .sort(sortDesc("finalizedAt")),
      };

      setColumns((prev) => ({
        ...prev,
        NA: { ...prev.NA, items: groupedOrders.NA },
        AS: { ...prev.AS, items: groupedOrders.AS },
        IP: { ...prev.IP, items: groupedOrders.IP },
        CL: { ...prev.CL, items: groupedOrders.CL },
      }));
    },
    [filters, driversDetailsById]
  );

  useEffect(() => {
    if (finalizationErrorFleetRef.current !== fleetId) {
      finalizationErrorFleetRef.current = fleetId;
      finalizationErrorSignaturesRef.current = {};
    }
    if (!fleetId) {
      activationErrorSignaturesRef.current = {};
      hasLoadedActivationErrorsRef.current = false;
      return undefined;
    }

    activationErrorSignaturesRef.current = {};
    hasLoadedActivationErrorsRef.current = false;

    const ordersRef = collection(db, "fleets", fleetId, "transport_orders");
    const unsubscribe = onSnapshot(
      ordersRef,
      (snapshot) => {
        const transportOrders = snapshot.docs.map((doc) => {
          const raw = doc.data();
          return {
            ref: raw.internalReference || raw.customerReference || raw.transportOrderRef || doc.id,
            id: doc.id,
            status: raw.status,
            raw,
          };
        });

        const nextActivationErrors = {};
        const nextFinalizationErrors = {};
        for (const order of transportOrders) {
          const error = order.status === "ACTIVE" ? order.raw?.finalizationError : null;
          if (!error) continue;
          const signature = error.messageKey || "toast.automaticFinalizationVerificationFailed";
          nextFinalizationErrors[order.id] = signature;
          if (finalizationErrorSignaturesRef.current[order.id] !== signature) {
            toast({
              title: order.ref || order.id,
              description: t(signature),
              status: "warning",
            });
          }
        }
        finalizationErrorSignaturesRef.current = nextFinalizationErrors;
        for (const order of transportOrders) {
          const activationError = order?.raw?.activationError;
          if (!activationError) continue;
          const errorSignature = [
            activationError?.messageKey || "",
            activationError?.message || "",
            activationError?.at?.seconds ||
              activationError?.at?._seconds ||
              activationError?.at?.toMillis?.() ||
              "",
          ].join("|");
          nextActivationErrors[order.id] = errorSignature;
        }

        if (hasLoadedActivationErrorsRef.current && !bulkActivationRunningRef.current) {
          for (const order of transportOrders) {
            const activationError = order?.raw?.activationError;
            if (!activationError) continue;
            const currentSignature = nextActivationErrors[order.id];
            const previousSignature = activationErrorSignaturesRef.current[order.id];
            if (currentSignature === previousSignature) continue;

            const message = getTranslatedMessage(
              activationError?.messageKey,
              t("toast.errorActivatingOrder")
            );

            toast({
              title: message,
              reference: order.raw?.internalReference || order.ref || order.id,
              status: "warning",
            });
          }
        } else {
          hasLoadedActivationErrorsRef.current = true;
        }

        activationErrorSignaturesRef.current = nextActivationErrors;

        setAllTransportOrders(transportOrders);
        updateColumnItems(transportOrders);
      },
      (error) => {
        console.error("Failed to listen to transport orders:", error);
      }
    );

    return () => unsubscribe();
  }, [fleetId, getTranslatedMessage, t, toast, updateColumnItems]);

  const onDragEnd = async (result) => {
    setAllowedDropIds([]);
    if (bulkActivationRunningRef.current) return;
    if (!result.destination) return;
    const { source, destination } = result;

    if (source.droppableId === destination.droppableId) {
      return;
    }

    const sourceColumn = columns[source.droppableId];
    const sourceItems = [...sourceColumn.items];
    const [moved] = sourceItems.splice(source.index, 1);
    if (!moved) return;

    if (source.droppableId === "CL") {
      return;
    }

    if (source.droppableId === "NA" && destination.droppableId === "IP") {
      return;
    }

    if (source.droppableId === "NA" && destination.droppableId === "AS") {
      setAssignTarget(moved);
      setAssignDriverId(null);
      setIsAssignModalOpen(true);
      return;
    }

    if (destination.droppableId === "CL" && moved.raw?.freightDocumentId) {
      const outcome = await updateStatus(moved, "FINALIZED", moved.raw.status, true);
      if (outcome !== "NEEDS_CONFIRMATION") return;
    }

    if (source.droppableId === "NA" && destination.droppableId === "CL") {
      setFinalizeTarget(moved);
      setFinalizeFromStatus("PENDING");
      setFinalizeReason("");
      setEDocFinalizationAction("DECLARE_DELIVERED");
      setDeliveryConfirmed(false);
      setIsFinalizeConfirmOpen(true);
      return;
    }

    if (source.droppableId === "AS" && destination.droppableId === "NA") {
      (async () => {
        await removeAssignDriver(moved);
      })();
      return;
    }

    if (source.droppableId === "AS" && destination.droppableId === "IP") {
      updateStatus(moved, "ACTIVE", "ASSIGNED");
      return;
    }

    if (source.droppableId === "AS" && destination.droppableId === "CL") {
      setFinalizeTarget(moved);
      setFinalizeFromStatus("ASSIGNED");
      setFinalizeReason("");
      setEDocFinalizationAction("DECLARE_DELIVERED");
      setDeliveryConfirmed(false);
      setIsFinalizeConfirmOpen(true);
      return;
    }

    if (source.droppableId === "IP" && destination.droppableId === "CL") {
      setFinalizeTarget(moved);
      setFinalizeFromStatus("ACTIVE");
      setFinalizeReason("");
      setEDocFinalizationAction("DECLARE_DELIVERED");
      setDeliveryConfirmed(false);
      setIsFinalizeConfirmOpen(true);
      return;
    }

    if (source.droppableId === "IP") {
      return;
    }
  };
  const onDragStart = (start) => {
    setActiveOrderDetailId(null);

    const sourceId = start.source?.droppableId;
    const sourceIndex = start.source?.index;
    if (!sourceId) {
      setAllowedDropIds([]);
      return;
    }

    if (sourceId === "NA") {
      setAllowedDropIds(["AS", "CL"]);
      return;
    }
    if (sourceId === "AS") {
      const moved = columns[sourceId]?.items?.[sourceIndex];
      const canActivate = !!normalizeId(moved?.raw?.userId);
      setAllowedDropIds(canActivate ? ["NA", "IP", "CL"] : ["NA", "CL"]);
      return;
    }
    if (sourceId === "IP") {
      setAllowedDropIds(["CL"]);
      return;
    }
    if (sourceId === "CL") {
      setAllowedDropIds([]);
      return;
    }
  };

  const buildStructuredGoods = useCallback((formData) => {
    if (Array.isArray(formData?.structuredGoods) && formData.structuredGoods.length > 0) {
      return formData.structuredGoods
        .map((item) =>
          Object.fromEntries(
            Object.entries(item || {}).filter(
              ([, value]) => value !== undefined && value !== null && value !== ""
            )
          )
        )
        .filter((item) => Boolean(item.name));
    }

    if (formData?.goods) {
      return [{ name: String(formData.goods).trim() }];
    }

    return [];
  }, []);

  const buildEDocTemplates = useCallback((profile) => {
    switch (profile) {
      case "ECMR":
        return { pdfTemplate: "ECMR", docType: "WAYBILL", docTemplate: "WAYBILL" };
      case "EUROPEENNE":
        return { pdfTemplate: "EUROPEENNE", docType: "WAYBILL_FRA", docTemplate: "WAYBILL_FRA" };
      case "CARTA_DE_PORTE":
        return { pdfTemplate: "CARTA_DE_PORTE", docType: "WAYBILL_ESP", docTemplate: "WAYBILL_ESP" };
      case "DOCUMENTO_DE_CONTROL":
        return {
          pdfTemplate: "DOCUMENTO_DE_CONTROL",
          docType: "WAYBILL_ESP",
          docTemplate: "WAYBILL_ESP",
        };
      case "NAKLADNI_LIST_CZ":
        return {
          pdfTemplate: "NAKLADNI_LIST_CZ",
          docType: "WAYBILL_CZE",
          docTemplate: "WAYBILL_CZE",
        };
      case "LIEFERSCHEIN":
        return { pdfTemplate: "LIEFERSCHEIN", docType: "WAYBILL", docTemplate: "WAYBILL" };
      default:
        return {};
    }
  }, []);

  const resetCreateOrderDraft = useCallback(() => {
    setImportedOrderDraft(null);
    setImportedOrderDraftId("");
    setOrderFormKey(`create-empty-${Date.now()}`);
  }, []);

  const loadOrderDrafts = useCallback(async () => {
    if (!fleetId) return;
    setOrderDraftsLoading(true);
    try {
      const endpoint = API_FleetTransportOrderDrafts.replace("{fleetId}", fleetId);
      const processReadyEndpoint = API_FleetTransportOrderDrafts_processReady.replace("{fleetId}", fleetId);
      try {
        await API_REQUEST_HEADERS_BASEURL.post(processReadyEndpoint);
      } catch (error) {
        console.error("Error processing ready transport order drafts:", error);
      }
      const response = await API_REQUEST_HEADERS_BASEURL.get(endpoint, {
        params: { status: "DRAFT" },
      });
      setOrderDrafts(Array.isArray(response?.data?.data) ? response.data.data : []);
    } catch (error) {
      console.error("Error loading transport order drafts:", error);
    } finally {
      setOrderDraftsLoading(false);
    }
  }, [fleetId]);

  const loadOrderImportLogs = useCallback(async () => {
    if (!fleetId) return;
    setOrderImportLogsLoading(true);
    try {
      const endpoint = API_FleetTransportOrderDrafts.replace("{fleetId}", fleetId);
      const response = await API_REQUEST_HEADERS_BASEURL.get(endpoint, {
        params: { status: "ALL" },
      });
      const rows = Array.isArray(response?.data?.data) ? response.data.data : [];
      setOrderImportLogs(rows);
      if (selectedImportLog) {
        setSelectedImportLog(rows.find((row) => row.id === selectedImportLog.id) || null);
      }
    } catch (error) {
      console.error("Error loading transport order import logs:", error);
      toast({ error,
        title: t("orderImport.logsLoadError", {
          defaultValue: "Could not load incoming order logs.",
        }),
        status: "warning",
      });
    } finally {
      setOrderImportLogsLoading(false);
    }
  }, [fleetId, selectedImportLog, t, toast]);

  const loadOrderImportSettings = useCallback(async () => {
    if (!fleetId) return;
    setOrderImportSettingsLoading(true);
    try {
      const endpoint = API_FleetTransportOrderDrafts_settings.replace("{fleetId}", fleetId);
      const response = await API_REQUEST_HEADERS_BASEURL.get(endpoint);
      const specialInstructions = String(response?.data?.data?.specialInstructions || "");
      const placeMatchMemory = Array.isArray(response?.data?.data?.placeMatchMemory) ? response.data.data.placeMatchMemory : [];
      setOrderImportSpecialInstructions(specialInstructions);
      setDraftOrderImportSpecialInstructions(specialInstructions);
      setOrderImportPlaceMatchMemory(placeMatchMemory);
      setDraftOrderImportPlaceMatchMemory(placeMatchMemory);
    } catch (error) {
      console.error("Error loading order import settings:", error);
      toast({ error,
        title: t("orderImport.instructionsLoadError", {
          defaultValue: "Could not load import instructions.",
        }),
        status: "warning",
      });
    } finally {
      setOrderImportSettingsLoading(false);
    }
  }, [fleetId, t, toast]);

  const openOrderImportSettings = async () => {
    setIsOrderImportSettingsOpen(true);
    await loadOrderImportSettings();
  };

  const saveOrderImportSettings = async () => {
    if (!fleetId) return;
    setOrderImportSettingsSaving(true);
    try {
      const endpoint = API_FleetTransportOrderDrafts_settings.replace("{fleetId}", fleetId);
      const response = await API_REQUEST_HEADERS_BASEURL.patch(endpoint, {
        specialInstructions: draftOrderImportSpecialInstructions,
        placeMatchMemory: draftOrderImportPlaceMatchMemory,
      });
      const specialInstructions = String(response?.data?.data?.specialInstructions || "");
      const placeMatchMemory = Array.isArray(response?.data?.data?.placeMatchMemory) ? response.data.data.placeMatchMemory : [];
      setOrderImportSpecialInstructions(specialInstructions);
      setDraftOrderImportSpecialInstructions(specialInstructions);
      setOrderImportPlaceMatchMemory(placeMatchMemory);
      setDraftOrderImportPlaceMatchMemory(placeMatchMemory);
      setIsOrderImportSettingsOpen(false);
      toast({
        title: t("orderImport.instructionsSaved", { defaultValue: "Import instructions saved." }),
        status: "success",
      });
    } catch (error) {
      console.error("Error saving order import settings:", error);
      toast({ error,
        title: t("orderImport.instructionsSaveError", {
          defaultValue: "Could not save import instructions.",
        }),
        status: "warning",
      });
    } finally {
      setOrderImportSettingsSaving(false);
    }
  };

  const loadOrderEmailAlias = useCallback(async () => {
    if (!fleetId) return;
    setOrderEmailAliasLoading(true);
    try {
      const endpoint = API_FleetOrderEmailAlias.replace("{fleetId}", fleetId);
      const response = await API_REQUEST_HEADERS_BASEURL.get(endpoint);
      setOrderEmailAlias(response?.data?.data || null);
    } catch (error) {
      console.error("Error loading fleet order email alias:", error);
      setOrderEmailAlias(null);
    } finally {
      setOrderEmailAliasLoading(false);
    }
  }, [fleetId]);

  useEffect(() => {
    if (isCreateModalOpen) {
      setOrderDraftsPage(1);
      loadOrderDrafts();
      loadOrderEmailAlias();
      loadOrderImportSettings();
    }
  }, [isCreateModalOpen, loadOrderDrafts, loadOrderEmailAlias, loadOrderImportSettings]);

  useEffect(() => {
    const lastPage = Math.max(1, Math.ceil(orderDrafts.length / 6));
    setOrderDraftsPage((page) => Math.min(page, lastPage));
  }, [orderDrafts.length]);

  useEffect(() => {
    loadOrderEmailAlias();
  }, [loadOrderEmailAlias]);

  const copyOrderEmailAlias = useCallback(async () => {
    const address = String(orderEmailAlias?.alias?.address || "").trim();
    if (!address) return;
    const copyWithSelectionFallback = () => {
      const textArea = document.createElement("textarea");
      textArea.value = address;
      textArea.setAttribute("readonly", "");
      textArea.style.position = "fixed";
      textArea.style.top = "-9999px";
      document.body.appendChild(textArea);
      textArea.select();
      const copied = document.execCommand("copy");
      document.body.removeChild(textArea);
      if (!copied) {
        throw new Error("Copy command failed");
      }
    };

    try {
      if (navigator.clipboard?.writeText) {
        try {
          await navigator.clipboard.writeText(address);
        } catch (error) {
          copyWithSelectionFallback();
        }
      } else {
        copyWithSelectionFallback();
      }
      toast({
        title: t("transportOrderEmailAlias.copied", { defaultValue: "Order email copied." }),
        status: "success",
      });
    } catch (error) {
      toast({ error,
        title: t("transportOrderEmailAlias.copyFailed", { defaultValue: "Could not copy the order email address." }),
        status: "warning",
      });
    }
  }, [orderEmailAlias?.alias?.address, t, toast]);

  const renderOrderEmailAliasContent = () => {
    const address = String(orderEmailAlias?.alias?.address || "").trim();
    return (
      <Space direction="vertical" size={8} style={{ width: "100%" }}>
        <Typography.Text type="secondary">
          {t("transportOrderEmailAlias.hint", {
            defaultValue: "Forward order emails, PDFs, or images here. Gandolapp will create drafts for review.",
          })}
        </Typography.Text>
        <Space wrap>
          <Typography.Text code copyable={false}>
            {address || (orderEmailAliasLoading
              ? t("transportOrderEmailAlias.loading", { defaultValue: "Preparing order email..." })
              : t("transportOrderEmailAlias.unavailable", { defaultValue: "Order email is not available right now." }))}
          </Typography.Text>
          {address ? (
            <Button size="small" icon={<CopyOutlined />} onClick={copyOrderEmailAlias}>
              {t("transportOrderEmailAlias.copy", { defaultValue: "Copy email" })}
            </Button>
          ) : null}
        </Space>
      </Space>
    );
  };


  const safeOrderImportName = (fileName = "") =>
    String(fileName || "order-import")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-zA-Z0-9._-]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 90) || "order-import";

  const uploadOrderImportFiles = async (files = []) => {
    const uploaded = [];
    for (const file of files) {
      const rawFile = file.originFileObj || file;
      const path = `fleets/${fleetId}/order_imports/${Date.now()}-${Math.round(Math.random() * 10000)}-${safeOrderImportName(rawFile.name)}`;
      const ref = storageRef(storage, path);
      await uploadBytes(ref, rawFile, {
        contentType: rawFile.type || "application/octet-stream",
      });
      const url = await getDownloadURL(ref);
      uploaded.push({
        fileLocation: path,
        url,
        name: rawFile.name || file.name || "",
        contentType: rawFile.type || file.type || "",
      });
    }
    return uploaded;
  };

  const getImportSourceType = (draft = {}) => {
    if (draft?.sourceOrigin?.type === "EMAIL") return "EMAIL";
    const sourceType = String(draft?.sourceType || "").toUpperCase();
    if (sourceType === "IMAGES" || sourceType === "IMAGE") return "IMAGE";
    return sourceType || "UNKNOWN";
  };

  const getImportSourceIcon = (draft = {}) => {
    const sourceType = getImportSourceType(draft);
    if (sourceType === "EMAIL") return <MailOutlined />;
    if (sourceType === "TEXT") return <FileTextOutlined />;
    if (sourceType === "PDF") return <FilePdfOutlined />;
    if (sourceType === "IMAGE") return <PictureOutlined />;
    return <FileOutlined />;
  };

  const getImportSourceLabel = (draft = {}) => {
    const sourceType = getImportSourceType(draft);
    if (sourceType === "EMAIL") return t("orderImport.sourceEmail", { defaultValue: "Email" });
    if (sourceType === "TEXT") return t("orderImport.sourceText", { defaultValue: "Text" });
    if (sourceType === "PDF") return t("orderImport.sourcePdf", { defaultValue: "PDF" });
    if (sourceType === "IMAGE") return t("orderImport.sourceImage", { defaultValue: "Image" });
    return t("orderImport.sourceUnknown", { defaultValue: "Import" });
  };

  const formatImportDate = (value) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return date.toLocaleString();
  };

  const openOrderImportLogs = async () => {
    setIsImportLogsOpen(true);
    setSelectedImportLog(null);
    setOrderImportReferenceFilter("");
    setOrderImportLogsPage(1);
    await loadOrderImportLogs();
  };

  const openOrderImportSource = (draft) => {
    setSelectedImportLog(draft);
    setOrderImportReferenceFilter("");
    setOrderImportLogsPage(1);
    setIsImportLogsOpen(true);
  };

  const filteredOrderImportLogs = useMemo(() => {
    const filter = orderImportReferenceFilter.trim().toLocaleLowerCase();
    if (!filter) return orderImportLogs;
    return orderImportLogs.filter((draft) => [
      draft?.draft?.internalReference,
      draft?.draft?.customerReference,
      draft?.draft?.transportOrderRef,
    ].some((reference) => String(reference || "").toLocaleLowerCase().includes(filter)));
  }, [orderImportLogs, orderImportReferenceFilter]);

  const openOrderImportSourceFile = async (file = {}) => {
    const pendingWindow = window.open("", "_blank");
    if (pendingWindow) pendingWindow.opener = null;
    try {
      const url = file.url || (file.fileLocation
        ? await getDownloadURL(storageRef(storage, file.fileLocation))
        : "");
      if (!url) throw new Error("Source file is unavailable");
      if (pendingWindow) pendingWindow.location.replace(url);
      else window.open(url, "_blank", "noopener,noreferrer");
    } catch (error) {
      pendingWindow?.close();
      console.error("Error opening imported order source file:", error);
      toast({ error,
        title: t("orderImport.sourceOpenError", {
          defaultValue: "Could not open the original file.",
        }),
        status: "warning",
      });
    }
  };

  const getOrderImportSourceFileName = (file = {}) => {
    if (file.name || file.filename) return file.name || file.filename;
    const storedName = String(file.fileLocation || "").split("/").pop() || "";
    const storedPrefix = file.id ? `${file.id}-` : "";
    return storedPrefix && storedName.startsWith(storedPrefix)
      ? storedName.slice(storedPrefix.length)
      : storedName;
  };

  const renderImportSourceDetails = (draft = {}) => {
    const emailOrigin = draft?.sourceOrigin?.type === "EMAIL" ? draft.sourceOrigin : null;
    const sourceFiles = Array.isArray(draft?.sourceFiles) ? draft.sourceFiles : [];
    const sourceText = String(draft?.sourceText || "").trim();

    return (
      <Space direction="vertical" size={12} style={{ width: "100%" }}>
        {emailOrigin ? (
          <div>
            <Typography.Text strong>
              {t("orderImport.originalEmail", { defaultValue: "Original email" })}
            </Typography.Text>
            <div>
              <Typography.Text type="secondary">
                {t("transportOrderEmailAlias.fromSender", {
                  sender: emailOrigin.sender || "-",
                  defaultValue: `From ${emailOrigin.sender || "-"}`,
                })}
              </Typography.Text>
            </div>
            <div>
              <Typography.Text type="secondary">
                {t("transportOrderEmailAlias.subjectLine", {
                  subject: emailOrigin.subject || "-",
                  defaultValue: `Subject: ${emailOrigin.subject || "-"}`,
                })}
              </Typography.Text>
            </div>
          </div>
        ) : null}

        {sourceText ? (
          <div>
            <Typography.Text strong>
              {t("orderImport.originalText", { defaultValue: "Original text" })}
            </Typography.Text>
            <pre
              style={{
                marginTop: 8,
                maxHeight: 260,
                overflow: "auto",
                whiteSpace: "pre-wrap",
                background: "#f8fafc",
                border: "1px solid #e5e7eb",
                borderRadius: 8,
                padding: 12,
              }}
            >
              {sourceText}
            </pre>
          </div>
        ) : null}

        {sourceFiles.length > 0 ? (
          <div>
            <Typography.Text strong>
              {t("orderImport.originalFiles", { defaultValue: "Original files" })}
            </Typography.Text>
            <List
              size="small"
              dataSource={sourceFiles}
              renderItem={(file) => (
                <List.Item
                  actions={[
                    file.url || file.fileLocation ? (
                      <Button size="small" type="link" onClick={() => openOrderImportSourceFile(file)} icon={<EyeOutlined />}>
                        {t("orderImport.openSource", { defaultValue: "Open" })}
                      </Button>
                    ) : null,
                  ].filter(Boolean)}
                >
                  <List.Item.Meta
                    avatar={String(file.contentType || "").startsWith("image/") ? <PictureOutlined /> : <FileOutlined />}
                    title={file.url || file.fileLocation ? (
                      <Typography.Link onClick={() => openOrderImportSourceFile(file)}>
                        {getOrderImportSourceFileName(file) || t("orderImport.sourceFile", { defaultValue: "Source file" })}
                      </Typography.Link>
                    ) : (
                      getOrderImportSourceFileName(file) || t("orderImport.sourceFile", { defaultValue: "Source file" })
                    )}
                    description={file.contentType || file.fileLocation || ""}
                  />
                </List.Item>
              )}
            />
          </div>
        ) : null}

        {!sourceText && sourceFiles.length === 0 && !emailOrigin ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={t("orderImport.noOriginalSource", {
              defaultValue: "No original source data is available for this import.",
            })}
          />
        ) : null}
      </Space>
    );
  };

  const applyExtractedOrderDraft = async (sourceDraftRecord, { notify = true } = {}) => {
    let draftRecord = sourceDraftRecord;
    const sourceDraftId = sourceDraftRecord?.id || "";
    if (fleetId && sourceDraftId) {
      try {
        const endpoint = API_FleetTransportOrderDraft_review
          .replace("{fleetId}", fleetId)
          .replace("{draftId}", sourceDraftId);
        const response = await API_REQUEST_HEADERS_BASEURL.post(endpoint);
        draftRecord = response?.data?.data || sourceDraftRecord;
      } catch (error) {
        toast({ error,
          title: t("orderImport.directoryReviewError"),
          status: "warning",
        });
      }
    }
    const draft = draftRecord?.draft || draftRecord || {};
    setImportedOrderDraft({
      ...draft,
      importDraftId: draftRecord?.id || sourceDraftId,
      directoryResolution: draftRecord?.directoryResolution || {},
      extractedDirectoryValues: draftRecord?.extractedDirectoryValues || {},
    });
    setImportedOrderDraftId(draftRecord?.id || sourceDraftId);
    setOrderFormKey(`create-import-${draftRecord?.id || sourceDraftId || Date.now()}`);
    if (draftRecord?.referenceConflict?.messageKey || draftRecord?.supersedesDraftIds?.length) {
      toast({ title: t("orderImport.reviewImportedOrder"), description: t(draftRecord?.referenceConflict?.messageKey || "orderImport.pendingReplaced"), status: "warning" });
    } else if (notify && Array.isArray(draftRecord?.warnings) && draftRecord.warnings.length > 0) {
      const localizedWarnings = localizeOrderImportWarnings(draftRecord.warnings, t);
      toast({
        title: t("orderImport.reviewImportedOrder"),
        description: localizedWarnings.join(". "),
        status: "warning",
      });
    } else if (notify) {
      toast({
        title: t("orderImport.orderImported"),
        description: t("orderImport.reviewDetectedFields"),
        status: "success",
      });
    }
    return draftRecord;
  };

  const extractOrderDraft = async () => {
    if (!fleetId || isExtractingOrder) return;
    setIsExtractingOrder(true);
    try {
      const endpoint = API_FleetTransportOrderDrafts_extract.replace("{fleetId}", fleetId);
      let body = { sourceType: orderImportMode };
      if (orderImportMode === "TEXT") {
        body = { sourceType: "TEXT", text: orderImportText, specialInstructions: orderImportSpecialInstructions };
      } else {
        const uploadedFiles = await uploadOrderImportFiles(orderImportFiles);
        body = { sourceType: orderImportMode, files: uploadedFiles, specialInstructions: orderImportSpecialInstructions };
      }
      const response = await API_REQUEST_HEADERS_BASEURL.post(endpoint, body);
      const draftRecord = response?.data?.data;
      setOrderImportText("");
      setOrderImportFiles([]);
      if (draftRecord?.status === "ACCEPTED" && draftRecord?.autoCreatedTransportOrderId) {
        resetCreateOrderDraft();
        toast({
          title: t(draftRecord.importUpdated ? "orderImport.existingUpdated" : "orderImport.autoImportedTitle", { defaultValue: "Order imported" }),
          description: draftRecord.importUpdated ? undefined : t("orderImport.autoImportedDescription", {
            defaultValue: "All imported values matched, so the order was added directly to the board.",
          }),
          status: "success",
        });
      } else {
        await applyExtractedOrderDraft(draftRecord);
      }
      await loadOrderDrafts();
    } catch (error) {
      toast({ error,
        title: t("feedback.importFailed"),
        status: "warning",
      });
    } finally {
      setIsExtractingOrder(false);
    }
  };

  const confirmPlaceMatchRules = (rules = []) => {
    if (!rules.length) return Promise.resolve([]);
    return new Promise((resolve) => {
      let selectedKeys = [];
      const rulesByKey = new Map(rules.map((rule) => [placeRuleKey(rule), rule]));
      let settled = false;
      const finish = (value) => {
        if (settled) return;
        settled = true;
        resolve(value);
      };
      Modal.confirm({
        title: t("orderImport.placeRulePromptTitle", { defaultValue: "Save these matching suggestions?" }),
        content: (
          <Space direction="vertical" style={{ width: "100%" }}>
            <Typography.Text>
              {t("orderImport.placeRulePromptDescription", {
                defaultValue: "Select only the suggestions you want to save for future imports. Unselected suggestions apply only to this order.",
              })}
            </Typography.Text>
            <Checkbox.Group
              defaultValue={selectedKeys}
              onChange={(values) => { selectedKeys = values; }}
              style={{ display: "grid", gap: 8 }}
            >
              {rules.map((rule) => (
                <Checkbox key={placeRuleKey(rule)} value={placeRuleKey(rule)}>
                  <Typography.Text code>{rule.sourceName}{rule.sourceCode ? ` (${rule.sourceCode})` : ""}</Typography.Text>
                  {" → "}{rule.placeName}
                </Checkbox>
              ))}
            </Checkbox.Group>
          </Space>
        ),
        okText: t("orderImport.saveSelectedPlaceRules", { defaultValue: "Save selected suggestions" }),
        cancelText: t("orderImport.skipPlaceRules", { defaultValue: "Do not save suggestions" }),
        zIndex: CREATE_ORDER_SECONDARY_MODAL_Z_INDEX,
        onOk: () => finish(selectedKeys.map((key) => rulesByKey.get(key)).filter(Boolean)),
        onCancel: () => finish([]),
      });
    });
  };

  const markOrderDraftStatus = async (draftId, status, createdTransportOrderId = "", approvedPlaceMatchSelections = []) => {
    if (!fleetId || !draftId) return;
    try {
      const endpoint = API_FleetTransportOrderDraft
        .replace("{fleetId}", fleetId)
        .replace("{draftId}", draftId);
      await API_REQUEST_HEADERS_BASEURL.patch(endpoint, { status, createdTransportOrderId, approvedPlaceMatchSelections });
      await loadOrderDrafts();
    } catch (error) {
      toast({ error,
        title: t("feedback.draftSave"),
        status: "warning",
      });
    }
  };

  const reprocessOrderDraft = async (draft) => {
    if (!fleetId || !draft?.id || reprocessingOrderDraftId) return;
    setReprocessingOrderDraftId(draft.id);
    try {
      const endpoint = API_FleetTransportOrderDraft_reprocess
        .replace("{fleetId}", fleetId)
        .replace("{draftId}", draft.id);
      const response = await API_REQUEST_HEADERS_BASEURL.post(endpoint);
      const reprocessed = response?.data?.data || {};
      const reviewedDraft = await applyExtractedOrderDraft(reprocessed, { notify: false });
      setOrderDrafts((currentDrafts) => currentDrafts.map((currentDraft) =>
        currentDraft.id === draft.id ? reviewedDraft : currentDraft
      ));
      setSelectedImportLog((currentDraft) =>
        currentDraft?.id === draft.id ? reviewedDraft : currentDraft
      );
      toast({
        title: t("orderImport.reprocessComplete", { defaultValue: "Order processed again" }),
        description: reprocessed.status === "ACCEPTED"
          ? t("orderImport.autoImportedDescription", {
              defaultValue: "All imported values matched, so the order was added directly to the board.",
            })
          : t("orderImport.reprocessReview", {
              defaultValue: "The new extraction is ready for review.",
            }),
        status: "success",
      });
    } catch (error) {
      toast({ error,
        title: t("orderImport.reprocessError", {
          defaultValue: "The order could not be processed again.",
        }),
        status: "warning",
      });
    } finally {
      setReprocessingOrderDraftId("");
    }
  };

  const createCard = async (formData, importUpdate = null) => {
    try {
      if (!fleetId) return false;
      const newAxios = API_REQUEST_HEADERS_BASEURL;
      const firstCheckpoint = formData?.checkPoints?.[0];
      const lat = parseFloat(firstCheckpoint?.latitude);
      const lon = parseFloat(firstCheckpoint?.longitude);

      const selectedDriverId = normalizeId(formData?.userId);
      if (selectedDriverId && !importedOrderDraftId) {
        const driverBlock = await verifyDriverAssignmentBlock(selectedDriverId);
        if (driverBlock) {
          toast({
            title: getTranslatedMessage(driverBlock.messageKey, driverBlock.defaultMessage),
            status: "warning",
          });
          return false;
        }
      }

      if (isNaN(lat) || isNaN(lon)) {
        toast({
          title: t("toast.errorSelectingLocation"),
          status: "warning",
        });
        return false;
      }

      const validPoints = (formData.checkPoints || []).filter(cp => cp && !cp.isWaypoint);
      const firstPoint = validPoints[0] || formData.checkPoints?.[0] || {};
      const lastPoint =
        validPoints[validPoints.length - 1] ||
        formData.checkPoints?.[formData.checkPoints.length - 1] ||
        {};
      const pickupPoint = formData.type === "CONTAINER_OUTBOUND" ? validPoints[1] || {} : firstPoint;
      const deliveryPoint = formData.type === "CONTAINER_INBOUND" ? validPoints[1] || {} : lastPoint;
      const structuredGoods = buildStructuredGoods(formData);
      const toTransfollowLocation = (location, fallback = {}) => ({
        name: fallback.name || location?.name || fallback.address || "",
        streetName: fallback.address || location?.address?.streetName || "",
        cityName: location?.address?.cityName || "",
        countryCode: fallback.countryCode || location?.address?.countryCode || "",
        latitude: fallback.latitude ?? location?.latitude ?? null,
        longitude: fallback.longitude ?? location?.longitude ?? null,
      });
      const directoryRefs = formData.directoryRefs || {
        customerId: formData.customerId || "",
        consignorId: formData.consignorId || "",
        consigneeId: formData.consigneeId || "",
        pickupLocationId: formData.pickupLocationId || "",
        dropoffLocationId: formData.dropoffLocationId || "",
      };
      const body = {
        ...(importedOrderDraftId ? { importedFrom: { type: "ORDER_IMPORT", draftId: importedOrderDraftId } } : {}),
        ...(importUpdate ? { confirmImportUpdate: true, expectedImportOrderId: importUpdate } : {}),
        transportOrderRef: formData.customerReference || formData.transportOrderRef,
        customerReference: formData.customerReference || formData.transportOrderRef,
        internalReference: formData.internalReference || "",
        checkPoints: formData.checkPoints.map((checkpoint) => ({
          latitude: parseFloat(checkpoint.latitude),
          longitude: parseFloat(checkpoint.longitude),
          address: checkpoint.address || "",
          name: checkpoint.name || checkpoint.address || "",
          countryCode: checkpoint.countryCode || "",
        })),
        truck: {
          headId: formData.headId,
          trailerId: formData.trailerId,
          plateSources: formData.plateSources,
        },
        type: formData.type,
        userId: formData.userId || "",
        activationAt: formData.activationAt,
        label: formData.label,
        generateEDocs: Boolean(formData.generateEDocs),
        generateECMR: Boolean(formData.generateEDocs),
        directoryRefs,
        customer: formData.customer || null,
        transfollow: {
          structuredGoods,
          carrierToDriverInstructions: String(formData.driverInstructions || "").trim(),
          specialAgreements: String(formData.specialAgreements || '').trim(),
          edocProfile: formData.eDocsProfile || "AUTO",
          ...buildEDocTemplates(formData.eDocsProfile),
          carrier: formData.effectiveCarrier || null,
          placeOfDelivery: toTransfollowLocation(formData.dropoffLocation, deliveryPoint),
          placeOfTakingOver: toTransfollowLocation(formData.pickupLocation, pickupPoint),
          consignor: {
            name: formData.consignor?.name || "",
            countryCode: formData.consignor?.address?.countryCode || firstPoint.countryCode || "",
          },
          consignee: {
            name: formData.consigneePartner?.name || formData.consignee || "",
            countryCode: formData.consigneePartner?.address?.countryCode || lastPoint.countryCode || "",
          },
        },
      };

      const isValid = (formData.checkPoints || []).every(
        (checkpoint) =>
          !isNaN(parseFloat(checkpoint.latitude)) && !isNaN(parseFloat(checkpoint.longitude))
      );
      if (!isValid) {
        toast({
          title: t("toast.errorSelectingLocation"),
          status: "warning",
        });
        return false;
      }
      const apiEndpoint = API_FleetDriver_ID_post_cards.replace("{fleetId}", fleetId);
      const response = await newAxios.post(apiEndpoint, body);
      const createdOrder = response?.data?.data || null;

      if (selectedDriverId && !normalizeId(createdOrder?.userId)) {
        toast({
          title: getTranslatedMessage(
            "toast.driverInAnotherFleet",
            "The order was created without driver assignment because the selected driver is not currently working in this fleet."
          ),
          status: "warning",
        });
      }

      toast({
        title: t(createdOrder?.importUpdated ? "orderImport.existingUpdated" : "toast.newOrderCreated"),
        reference: formData.internalReference || createdOrder?.internalReference,
        status: "success",
      });

      return createdOrder || true;
    } catch (error) {
      const messageKey = error?.response?.data?.messageKey;
      if (messageKey === "orderImport.existingEditable" && error.response.data.existingOrderId && !importUpdate) {
        const confirmed = await new Promise(resolve => Modal.confirm({
          title: t("orderImport.updateExistingTitle"), content: `${t("feedback.reference", { reference: formData.internalReference })}. ${t("orderImport.updateExistingConfirm")}`,
          okText: t("orderImport.updateExistingTitle"), cancelText: t("common.cancel"),
          zIndex: CREATE_ORDER_SECONDARY_MODAL_Z_INDEX,
          onOk: () => resolve(true), onCancel: () => resolve(false),
        }));
        return confirmed ? createCard(formData, error.response.data.existingOrderId) : false;
      }
      toast({
        title: fleetErrorMessage(t, error, t(importUpdate ? "toast.editOrderError" : "toast.errorOrder")),
        reference: formData.internalReference,
        status: "warning",
      });
    }

    return false;
  };

  const archiveAllFinalizedOrders = async () => {
    try {
      if (!fleetId) {
        return;
      }
      await assertFleetOperationsAllowed(fleetId);
      const finalizedOrdersQuery = query(
        collection(db, "fleets", fleetId, "transport_orders"),
        where("status", "==", "FINALIZED")
      );
      const finalizedOrdersSnapshot = await getDocs(finalizedOrdersQuery);

      if (finalizedOrdersSnapshot.empty) {
        toast({
          title: t("toast.noFinalizedOrders"),
          status: "warning",
        });
        return;
      }

      let batch = writeBatch(db);
      let operationCount = 0;

      for (const orderDoc of finalizedOrdersSnapshot.docs) {
        batch.update(orderDoc.ref, {
          status: "ARCHIVED",
          archivedAt: new Date(),
        });
        operationCount += 1;

        if (operationCount >= 500) {
          await batch.commit();
          batch = writeBatch(db);
          operationCount = 0;
        }
      }

      if (operationCount > 0) {
        await batch.commit();
      }

      toast({
        title: t("toast.ordersArchived"),
        status: "success",
      });
    } catch (error) {
      toast({ error,
        title: t("toast.errorArchivingOrders"),
        status: "warning",
      });
      console.error("Error archiving orders:", error);
    }
  };

  useEffect(() => {
    updateColumnItems(allTransportOrders);
  }, [filters, allTransportOrders, updateColumnItems]);

  useEffect(() => {
    if (!activeOrderDetailId) return;
    const exists = allTransportOrders.some((order) => order.id === activeOrderDetailId);
    if (!exists) {
      setActiveOrderDetailId(null);
    }
  }, [activeOrderDetailId, allTransportOrders]);

  useEffect(() => {
    const assistantOrderId = String(searchParams.get("assistantOrderId") || "").trim();
    const assistantOrderRef = String(searchParams.get("assistantOrderRef") || "").trim();
    const shouldOpenDetail = searchParams.get("assistantOrderDetail") === "1";
    const shouldOpenChat = searchParams.get("assistantOrderChat") === "1";
    const shouldOpenCreateOrder = searchParams.get("assistantCreateOrder") === "1";

    if (shouldOpenCreateOrder) {
      setIsCreateModalOpen(true);
    }

    if (!assistantOrderId && !assistantOrderRef && !shouldOpenCreateOrder) return;

    if (assistantOrderId || assistantOrderRef) {
      const orderIdLower = assistantOrderId.toLowerCase();
      const orderRefLower = assistantOrderRef.toLowerCase();
      const selectedOrder =
        (assistantOrderId
          ? allTransportOrders.find((order) => String(order.id || "").trim() === assistantOrderId)
          : null) ||
        (assistantOrderId
          ? allTransportOrders.find(
              (order) => String(order.ref || "").trim().toLowerCase() === orderIdLower
            )
          : null) ||
        (assistantOrderRef
          ? allTransportOrders.find(
              (order) => String(order.ref || "").trim().toLowerCase() === orderRefLower
            )
          : null) ||
        (assistantOrderId
          ? allTransportOrders.find(
              (order) =>
                String(order.raw?.transportOrderId || "")
                  .trim()
                  .toLowerCase() === orderIdLower
            )
          : null) ||
        null;

      if (selectedOrder) {
        if (shouldOpenDetail) {
          setActiveOrderDetailId(selectedOrder.id);
        }
        if (shouldOpenChat) {
          openOrderChat(selectedOrder);
        }
      }
    }

    const next = new URLSearchParams(searchParams);
    next.delete("assistantOrderId");
    next.delete("assistantOrderRef");
    next.delete("assistantOrderDetail");
    next.delete("assistantOrderChat");
    next.delete("assistantCreateOrder");
    setSearchParams(next, { replace: true });
  }, [allTransportOrders, openOrderChat, searchParams, setSearchParams]);

  const orderToolbarCategories = useMemo(
    () => [
      {
        key: "drivers",
        triggerLabel: t("contextToolbar.categories.drivers", { defaultValue: "Driver layers" }),
        triggerIcon: <TeamOutlined />,
        options: [],
        emptyText: t("contextToolbar.emptyDriversForOrders", {
          defaultValue: "No driver layer filters configured for orders yet.",
        }),
      },
      {
        key: "messages",
        triggerLabel: t("contextToolbar.categories.messages", {
          defaultValue: "Message filters",
        }),
        triggerIcon: <MessageOutlined />,
        options: [],
        emptyText: t("contextToolbar.emptyMessagesForPage", {
          defaultValue: "No message filters on this page yet.",
        }),
      },
    ],
    [t]
  );

  const isOrderDetailOpen = Boolean(activeOrderDetailId);

  return (
    <div className={isOrderDetailOpen ? "assignment-page assignment-page--detail-open" : "assignment-page"}>
      {isLoading && (
        <Typography.Paragraph>{t("common.loading", { defaultValue: "Loading..." })}</Typography.Paragraph>
      )}
      {!isLoading && !fleetId && (
        <Typography.Paragraph>
          {t("toast.missingFleet", { defaultValue: "Fleet is not configured yet." })}
        </Typography.Paragraph>
      )}
      {isLoading || !fleetId ? null : (
        <>
      <div className="assignment-toolbar-wrapper">
        <ContextToolbar
          searchSlot={
            <TransportOrderFilter
              compact
              onSelectDriver={({ query, selectedOrderId }) => {
                setFilters({
                  query: query || "",
                  selectedOrderId: selectedOrderId || "",
                });
              }}
            />
          }
          categories={orderToolbarCategories}
          actionsSlot={<BroadcastComponent tags={toolbarTags} drivers={driverList} compact />}
        />
      </div>

      <Modal
        open={bulkActivationOpen}
        title={t("transportManagement.bulkActivate")}
        onCancel={() => { if (!bulkActivationRunningRef.current) setBulkActivationOpen(false); }}
        closable={!bulkActivationProgress}
        maskClosable={!bulkActivationProgress}
        keyboard={!bulkActivationProgress}
        confirmLoading={!!bulkActivationProgress}
        cancelButtonProps={{ disabled: !!bulkActivationProgress }}
        okText={bulkActivationResults ? t("transportManagement.bulkActivationClose") : t("transportManagement.bulkActivate")}
        onOk={async () => {
          if (bulkActivationRunningRef.current) return;
          if (bulkActivationResults) { setBulkActivationOpen(false); return; }
          bulkActivationRunningRef.current = true;
          const orders = allTransportOrders.filter((order) => order.status === "ASSIGNED");
          setBulkActivationProgress({ done: 0, total: orders.length });
          try {
            const results = await activateAssignedOrders(orders, async (order) => {
              // Recheck stale cards before attempting activation. Filters do not
              // restrict this action: the confirmation explicitly covers all assigned orders.
              const snapshot = await getDoc(doc(db, "fleets", fleetId, "transport_orders", order.id));
              if (!snapshot.exists() || snapshot.data().status !== "ASSIGNED") {
                throw Object.assign(new Error("Order is no longer assigned"), { messageKey: "transportManagement.bulkNoLongerAssigned" });
              }
              const endpoint = API_FleetDriver_ID_patch_status_OT
                .replace("{fleetId}", fleetId)
                .replace("{transportOrderId}", order.id);
              await API_REQUEST_HEADERS_BASEURL.patch(endpoint, { newStatus: "ACTIVE", fromStatus: "ASSIGNED", expectedStatus: "ASSIGNED" });
            }, (done, total) => setBulkActivationProgress({ done, total }));
            setBulkActivationResults(results);
          } finally {
            bulkActivationRunningRef.current = false;
            setBulkActivationProgress(null);
          }
        }}
      >
        {bulkActivationResults ? (
          <>
            <p>{t("transportManagement.bulkActivationSummary", {
              activated: bulkActivationResults.filter((result) => result.activated).length,
              failed: bulkActivationResults.filter((result) => !result.activated).length,
            })}</p>
            <List
              style={{ maxHeight: 320, overflowY: "auto" }}
              dataSource={bulkActivationResults.filter((result) => !result.activated)}
              renderItem={({ order, error }) => (
                <List.Item><div><strong>{order.ref || order.id}</strong><br />{getTranslatedMessage(
                  error?.response?.data?.messageKey,
                  fleetErrorMessage(t, error, t("toast.errorActivatingOrder"))
                )}</div></List.Item>
              )}
            />
          </>
        ) : <p>{bulkActivationProgress
          ? t("transportManagement.bulkActivationProgress", bulkActivationProgress)
          : t("transportManagement.bulkActivationConfirm", { count: allTransportOrders.filter((order) => order.status === "ASSIGNED").length })}</p>}
      </Modal>

      <DragDropContext onDragStart={onDragStart} onDragEnd={onDragEnd}>
        <div style={{ display: "flex", justifyContent: "center", margin: "1rem" }}>
          <TaskColumnStyles>
            {Object.entries(columns).map(([columnId, column], index) => (
              <Droppable key={index} droppableId={columnId}>
                {(provided) => (
                  <TaskList
                    key={columnId}
                    columnId={columnId}
                    dimmed={
                      allowedDropIds.length > 0 && !allowedDropIds.includes(columnId)
                    }
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                  >
                    <div
                      key={column.id}
                      style={{
                        width: "100%",
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 8,
                        flexWrap: "wrap",
                        minHeight: 28,
                      }}
                    >
                      <Title>{column.title}</Title>

                      {columnId === "AS" && (
                        <ColumnActionButton
                          size="small"
                          icon={<PlayCircleOutlined />}
                          loading={!!bulkActivationProgress}
                          disabled={!fleetId || !allTransportOrders.some((order) => order.status === "ASSIGNED")}
                          onClick={() => { setBulkActivationResults(null); setBulkActivationOpen(true); }}
                        >
                          {t("transportManagement.bulkActivate")}
                        </ColumnActionButton>
                      )}

                      {column.title === t("transportManagement.pending") && (
                        <ColumnActionButton
                          size="small"
                          icon={<PlusOutlined />}
                          onClick={() => setIsCreateModalOpen(true)}
                        >
                          {t("transportCardForm.addNewTransportOrder")}
                        </ColumnActionButton>
                      )}

                      {column.title === t("transportManagement.finalized") && (
                        <ColumnActionButton
                          size="small"
                          icon={<InboxOutlined />}
                          disabled={!fleetId || !column.items.length}
                          onClick={archiveAllFinalizedOrders}
                        >
                          {t("transportManagement.archive")}
                        </ColumnActionButton>
                      )}
                    </div>

                    <Divider />
                    {column.items.map((item, index) => (
                      <KanbanCard
                        key={item.id}
                        item={item}
                        index={index}
                        drivers={driverList}
                        isOrderDetailOpen={activeOrderDetailId === item.id}
                        onOpenOrderDetail={(orderId) => setActiveOrderDetailId(orderId)}
                        onCloseOrderDetail={() => setActiveOrderDetailId(null)}
                        onAssignDriver={assignDriverFromCard}
                        onClearDriverClick={() => removeAssignDriver(item)}
                        onSetActiveClick={() => updateStatus(item, "ACTIVE")}
                        onFinalizedClick={() => updateStatus(item, "FINALIZED")}
                      />
                    ))}
                    {provided.placeholder}
                  </TaskList>
                )}
              </Droppable>
            ))}
          </TaskColumnStyles>
        </div>
      </DragDropContext>

      {/* Modal para crear nueva orden */}
      <Modal
        open={isCreateModalOpen}
        onCancel={() => {
          setIsCreateModalOpen(false);
          resetCreateOrderDraft();
        }}
        footer={null}
        width="min(1480px, calc(100vw - 32px))"
        centered
        destroyOnClose
        zIndex={CREATE_ORDER_MODAL_Z_INDEX}
        className="create-order-modal"
      >
        <Typography.Title level={4} style={{ textAlign: "center", marginBottom: 16 }}>
          {t("transportCardForm.createATransportOrder")}
        </Typography.Title>
        <div className="create-order-workspace">
          <aside className="create-order-inbox">
            <Space align="start" className="create-order-inbox-header">
              <div>
                <Typography.Title level={5} style={{ margin: 0 }}>
                  {t("orderImport.logsTitle", { defaultValue: "Incoming order logs" })}
                </Typography.Title>
                <Typography.Text type="secondary">
                  {orderDrafts.length}
                </Typography.Text>
              </div>
              <Space size={4} wrap>
                <Button size="small" icon={<SettingOutlined />} onClick={openOrderImportSettings}>
                  {t("orderImport.instructions", { defaultValue: "Tune processing" })}
                </Button>
                <Button size="small" icon={<HistoryOutlined />} onClick={openOrderImportLogs}>
                  {t("orderImport.logs", { defaultValue: "Incoming logs" })}
                </Button>
              </Space>
            </Space>

            <List
              className="create-order-draft-list"
              size="small"
              loading={orderDraftsLoading}
              locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("orderImport.noLogs", { defaultValue: "No imports yet." })} /> }}
              dataSource={orderDrafts}
              pagination={orderDrafts.length > 6 ? {
                current: orderDraftsPage,
                pageSize: 6,
                size: "small",
                showSizeChanger: false,
                onChange: setOrderDraftsPage,
              } : false}
              renderItem={(draft) => (
                <List.Item className={importedOrderDraftId === draft.id ? "create-order-draft-list__selected" : ""}>
                  <div className="create-order-draft-row">
                    <Space size={6} wrap>
                      <Typography.Text
                        strong
                        ellipsis
                        title={draft?.draft?.internalReference || draft?.draft?.customerReference || draft?.draft?.transportOrderRef || undefined}
                      >
                        {draft?.draft?.internalReference || draft?.draft?.customerReference || draft?.draft?.transportOrderRef || t("orderImport.missingReference", { defaultValue: "Reference missing" })}
                      </Typography.Text>
                      <Tag icon={getImportSourceIcon(draft)}>{getImportSourceLabel(draft)}</Tag>
                    </Space>
                    <Space size={0} wrap>
                      <Button size="small" type="link" onClick={() => applyExtractedOrderDraft(draft)}>
                        {t("orderImport.review", { defaultValue: "Review" })}
                      </Button>
                      <Button size="small" type="link" icon={<EyeOutlined />} onClick={() => openOrderImportSource(draft)}>
                        {t("orderImport.openSource", { defaultValue: "Open" })}
                      </Button>
                      <Button
                        size="small"
                        type="link"
                        icon={<ReloadOutlined />}
                        loading={reprocessingOrderDraftId === draft.id}
                        disabled={Boolean(reprocessingOrderDraftId && reprocessingOrderDraftId !== draft.id)}
                        onClick={() => reprocessOrderDraft(draft)}
                      >
                        {t("orderImport.reprocess", { defaultValue: "Process again" })}
                      </Button>
                      <Button size="small" type="link" danger onClick={() => markOrderDraftStatus(draft.id, "REJECTED")}>
                        {t("orderImport.reject", { defaultValue: "Reject" })}
                      </Button>
                    </Space>
                  </div>
                </List.Item>
              )}
            />

            <Collapse
              className="create-order-import-collapse"
              size="small"
              items={[{
                key: "import",
                label: t("orderImport.title", { defaultValue: "Incoming orders via" }),
                children: <>
                  <Typography.Paragraph type="secondary">
                    {t("orderImport.description", {
                      defaultValue: "Send orders by email, paste text, upload a PDF, or upload order photos. Gandolapp will fill a draft for review.",
                    })}
                  </Typography.Paragraph>
                  <Tabs
                    size="small"
                    activeKey={orderImportMode}
                    onChange={(key) => {
                      setOrderImportMode(key);
                      setOrderImportFiles([]);
                      setOrderImportText("");
                    }}
                    items={[
              {
                key: "EMAIL",
                label: (
                  <Space size={4}>
                    <MailOutlined />
                    <span>{t("orderImport.emailTab", { defaultValue: "Email" })}</span>
                  </Space>
                ),
                children: renderOrderEmailAliasContent(),
              },
              {
                key: "TEXT",
                label: t("orderImport.textTab", { defaultValue: "Text" }),
                children: (
                  <Input.TextArea
                    rows={4}
                    value={orderImportText}
                    onChange={(event) => setOrderImportText(event.target.value)}
                    placeholder={t("orderImport.textPlaceholder", {
                      defaultValue: "Paste order text copied from email, WhatsApp, or another system...",
                    })}
                  />
                ),
              },
              {
                key: "PDF",
                label: t("orderImport.pdfTab", { defaultValue: "PDF" }),
                children: (
                  <Upload.Dragger
                    accept="application/pdf"
                    beforeUpload={() => false}
                    maxCount={1}
                    fileList={orderImportFiles}
                    onChange={({ fileList }) => setOrderImportFiles(fileList)}
                  >
                    <p className="ant-upload-drag-icon"><InboxOutlined /></p>
                    <p className="ant-upload-text">
                      {t("orderImport.pdfDrop", { defaultValue: "Drop a PDF order here or click to browse" })}
                    </p>
                  </Upload.Dragger>
                ),
              },
              {
                key: "IMAGES",
                label: t("orderImport.imagesTab", { defaultValue: "Images" }),
                children: (
                  <Upload.Dragger
                    accept="image/*"
                    beforeUpload={() => false}
                    multiple
                    fileList={orderImportFiles}
                    onChange={({ fileList }) => setOrderImportFiles(fileList)}
                  >
                    <p className="ant-upload-drag-icon"><InboxOutlined /></p>
                    <p className="ant-upload-text">
                      {t("orderImport.imagesDrop", { defaultValue: "Drop order photos here or click to browse" })}
                    </p>
                  </Upload.Dragger>
                ),
              },
            ]}
                  />
                  {orderImportMode !== "EMAIL" ? (
                    <Button
                      type="primary"
                      loading={isExtractingOrder}
                      disabled={orderImportMode === "TEXT" ? !orderImportText.trim() : orderImportFiles.length === 0}
                      onClick={extractOrderDraft}
                    >
                      {t("orderImport.extract", { defaultValue: "Extract into draft" })}
                    </Button>
                  ) : null}
                </>,
              }]}
            />
          </aside>

          <section className="create-order-form-pane">
            {importedOrderDraft && (
              <div className="create-order-form-pane__toolbar">
                <Tag color="blue">{t("orderImport.importedDraft", { defaultValue: "Imported draft" })}</Tag>
                <Button size="small" onClick={resetCreateOrderDraft}>
                  {t("orderImport.clearDraft", { defaultValue: "Clear imported draft" })}
                </Button>
              </div>
            )}
            <OrderForm
              key={orderFormKey}
              initialValues={importedOrderDraft || {}}
              mode="create"
              drivers={driverList}
              defaultGenerateEDocs={Boolean(fleetData?.useEDOCs)}
              onSubmit={async (formData, submissionContext = {}) => {
                const approvedPlaceMatchSelections = importedOrderDraftId
                  ? await confirmPlaceMatchRules(collectPotentialPlaceMatchRules(
                    { directoryResolution: submissionContext.directoryResolution },
                    orderImportPlaceMatchMemory
                  ))
                  : [];
                const createdOrder = await createCard(formData);
                if (createdOrder) {
                  if (importedOrderDraftId) {
                    await markOrderDraftStatus(
                      importedOrderDraftId,
                      "ACCEPTED",
                      createdOrder?.transportOrderId || createdOrder?.id || "",
                      approvedPlaceMatchSelections
                    );
                  }
                  setIsCreateModalOpen(false);
                  resetCreateOrderDraft();
                }
              }}
              onCancel={() => {
                setIsCreateModalOpen(false);
                resetCreateOrderDraft();
              }}
              zIndex={1400}
            />
          </section>
        </div>
      </Modal>

      <Modal
        open={isOrderImportSettingsOpen}
        onCancel={() => {
          setDraftOrderImportSpecialInstructions(orderImportSpecialInstructions);
          setDraftOrderImportPlaceMatchMemory(orderImportPlaceMatchMemory);
          setIsOrderImportSettingsOpen(false);
        }}
        onOk={saveOrderImportSettings}
        confirmLoading={orderImportSettingsSaving}
        okText={t("orderImport.instructionsSave", { defaultValue: "Save instructions" })}
        cancelText={t("common.cancel", { defaultValue: "Cancel" })}
        width={640}
        centered
        zIndex={CREATE_ORDER_SECONDARY_MODAL_Z_INDEX}
        title={t("orderImport.instructionsTitle", { defaultValue: "Tune incoming-order processing" })}
      >
        <Space direction="vertical" size={12} style={{ width: "100%" }}>
          <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
            {t("orderImport.instructionsDescription", {
              defaultValue:
                "Use this for fleet-specific order formats. These instructions are appended to the extraction prompt for email, text, PDF, and image imports.",
            })}
          </Typography.Paragraph>
          <Input.TextArea
            rows={8}
            maxLength={4000}
            showCount
            disabled={orderImportSettingsLoading}
            value={draftOrderImportSpecialInstructions}
            onChange={(event) => setDraftOrderImportSpecialInstructions(event.target.value)}
            placeholder={t("orderImport.instructionsPlaceholder", {
              defaultValue:
                "Example: When the document mentions Import, use Container Import. The booking reference is usually labeled REF CLIENTE. Treat Depot as the empty-container return stop.",
            })}
          />
          <Alert
            type="info"
            showIcon
            message={t("orderImport.instructionsHint", {
              defaultValue:
                "Keep instructions short and factual. They should clarify recurring formats, not replace the source document.",
            })}
          />
          <div>
            <Space style={{ width: "100%", justifyContent: "space-between", marginBottom: 8 }}>
              <div>
                <Typography.Text strong>{t("orderImport.placeMemoryTitle")}</Typography.Text>
                <br />
                <Typography.Text type="secondary">{t("orderImport.placeMemoryDescription")}</Typography.Text>
              </div>
              <Button
                danger
                size="small"
                icon={<DeleteOutlined />}
                disabled={!draftOrderImportPlaceMatchMemory.length || orderImportSettingsLoading}
                onClick={() => setDraftOrderImportPlaceMatchMemory([])}
              >
                {t("orderImport.placeMemoryClear")}
              </Button>
            </Space>
            <List
              size="small"
              bordered
              style={{ maxHeight: 280, overflowY: "auto" }}
              locale={{ emptyText: t("orderImport.placeMemoryEmpty") }}
              dataSource={draftOrderImportPlaceMatchMemory}
              renderItem={(item, index) => (
                <List.Item
                  actions={[
                    <Button
                      key="remove"
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      aria-label={t("orderImport.placeMemoryRemove")}
                      onClick={() => setDraftOrderImportPlaceMatchMemory((current) => current.filter((_, position) => position !== index))}
                    />,
                  ]}
                >
                  <Space direction="vertical" size={4} style={{ width: "100%" }}>
                    <Input
                      value={item.sourceName}
                      addonBefore={t("orderImport.placeMemorySource")}
                      onChange={(event) => setDraftOrderImportPlaceMatchMemory((current) => current.map((entry, position) => (
                        position === index ? { ...entry, sourceName: event.target.value } : entry
                      )))}
                    />
                    <Input
                      value={item.placeName}
                      addonBefore={t("orderImport.placeMemoryTarget")}
                      onChange={(event) => setDraftOrderImportPlaceMatchMemory((current) => current.map((entry, position) => (
                        position === index ? { ...entry, placeName: event.target.value } : entry
                      )))}
                    />
                    {item.sourceCode ? <Typography.Text type="secondary">{t("orderImport.placeMemoryCode", { code: item.sourceCode })}</Typography.Text> : null}
                  </Space>
                </List.Item>
              )}
            />
          </div>
        </Space>
      </Modal>

      <Modal
        open={isImportLogsOpen}
        onCancel={() => {
          setIsImportLogsOpen(false);
          setSelectedImportLog(null);
          setOrderImportReferenceFilter("");
          setOrderImportLogsPage(1);
        }}
        footer={null}
        width={920}
        centered
        zIndex={CREATE_ORDER_SECONDARY_MODAL_Z_INDEX}
        title={t("orderImport.logsTitle", { defaultValue: "Incoming order logs" })}
      >
        <div style={{ display: "grid", gridTemplateColumns: "minmax(280px, 0.9fr) minmax(320px, 1.1fr)", gap: 16 }}>
          <div style={{ borderRight: "1px solid #e5e7eb", paddingRight: 12 }}>
            <Input
              allowClear
              value={orderImportReferenceFilter}
              onChange={(event) => {
                setOrderImportReferenceFilter(event.target.value);
                setOrderImportLogsPage(1);
              }}
              prefix={<SearchOutlined />}
              placeholder={t("orderImport.filterReference", { defaultValue: "Filter by reference" })}
              style={{ marginBottom: 12 }}
            />
            <List
              size="small"
              loading={orderImportLogsLoading}
              dataSource={filteredOrderImportLogs}
              pagination={{
                current: orderImportLogsPage,
                pageSize: 8,
                total: filteredOrderImportLogs.length,
                hideOnSinglePage: true,
                size: "small",
                onChange: setOrderImportLogsPage,
              }}
              locale={{
                emptyText: t("orderImport.noLogs", { defaultValue: "No imports yet." }),
              }}
              renderItem={(draft) => {
                const isSelected = selectedImportLog?.id === draft.id;
                const title = draft?.draft?.internalReference || draft?.draft?.customerReference || draft?.draft?.transportOrderRef || draft?.draft?.label || t("orderImport.importedDraft", { defaultValue: "Imported draft" });
                return (
                  <List.Item
                    onClick={() => setSelectedImportLog(draft)}
                    style={{
                      cursor: "pointer",
                      borderRadius: 8,
                      paddingInline: 8,
                      background: isSelected ? "#eef6ff" : "transparent",
                    }}
                  >
                    <List.Item.Meta
                      avatar={getImportSourceIcon(draft)}
                      title={
                        <Space size={6} wrap>
                          <span>{title}</span>
                          <Tag>{getImportSourceLabel(draft)}</Tag>
                          {draft.status ? <Tag color={draft.status === "DRAFT" ? "processing" : "default"}>{draft.status}</Tag> : null}
                        </Space>
                      }
                      description={formatImportDate(draft.createdAt || draft.updatedAt)}
                    />
                  </List.Item>
                );
              }}
            />
          </div>
          <div>
            {selectedImportLog ? (
              <Space direction="vertical" size={12} style={{ width: "100%" }}>
                <Space wrap style={{ justifyContent: "space-between", width: "100%" }}>
                  <div>
                    <Typography.Text strong>
                      {selectedImportLog?.draft?.internalReference || selectedImportLog?.draft?.customerReference || selectedImportLog?.draft?.transportOrderRef || selectedImportLog?.draft?.label || t("orderImport.importedDraft", { defaultValue: "Imported draft" })}
                    </Typography.Text>
                    <div>
                      <Typography.Text type="secondary">
                        {formatImportDate(selectedImportLog.createdAt || selectedImportLog.updatedAt)}
                      </Typography.Text>
                    </div>
                  </div>
                  <Button size="small" onClick={() => applyExtractedOrderDraft(selectedImportLog)}>
                    {t("orderImport.review", { defaultValue: "Review" })}
                  </Button>
                </Space>
                {renderImportSourceDetails(selectedImportLog)}
              </Space>
            ) : (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={t("orderImport.selectLog", {
                  defaultValue: "Select an import to inspect its original source.",
                })}
              />
            )}
          </div>
        </div>
      </Modal>

      <Modal
        open={isAssignModalOpen}
        onCancel={() => setIsAssignModalOpen(false)}
        centered
        title={t("transportManagement.assignDriver", { defaultValue: "Assign driver" })}
        footer={[
          <Button key="cancel" onClick={() => setIsAssignModalOpen(false)}>
            {t("setup.cancel", { defaultValue: "Cancel" })}
          </Button>,
          <Button
            key="assign"
            type="primary"
            onClick={assignDriverToOrder}
            disabled={!assignDriverId}
          >
            {t("transportCard.assignDriver", { defaultValue: "Assign driver" })}
          </Button>,
        ]}
      >
        <Typography.Paragraph style={{ marginBottom: 12 }}>
          {t("transportManagement.selectDriver", {
            defaultValue: "Select a driver for this transport order.",
          })}
        </Typography.Paragraph>
        {driverList.length > 0 && driverList.every((driver) => driver.disabled) && (
          <Typography.Text type="secondary">
            {t("toast.driverInAnotherFleet", {
              defaultValue: "The available drivers are not currently working in this fleet.",
            })}
          </Typography.Text>
        )}
        <Select
          placeholder={t("transportCard.driver", { defaultValue: "Driver" })}
          value={assignDriverId || undefined}
          onChange={(value) => setAssignDriverId(value)}
          options={driverList.map((driver) => ({
            label:
              driver.disabled && driver.disabledReason
                ? `${driver.name} - ${driver.disabledReason}`
                : driver.name,
            value: driver.id,
            disabled: driver.disabled,
          }))}
          showSearch
          optionFilterProp="label"
          style={{ width: "100%" }}
        />
      </Modal>

      <Modal
        open={isFinalizeConfirmOpen}
        onCancel={() => setIsFinalizeConfirmOpen(false)}
        centered
        title={t("transportManagement.confirmFinalizeTitle", {
          defaultValue: "Finalize order?",
        })}
        footer={[
          <Button key="cancel" onClick={() => setIsFinalizeConfirmOpen(false)}>
            {t("setup.cancel", { defaultValue: "Cancel" })}
          </Button>,
          <Button
            key="finalize"
            type="primary"
            onClick={async () => {
              let finalized = false;
              if (finalizeTarget && finalizeFromStatus) {
                finalized = await updateStatus(finalizeTarget, "FINALIZED", finalizeFromStatus);
              }
              if (!finalized) return;
              setIsFinalizeConfirmOpen(false);
              setFinalizeTarget(null);
              setFinalizeFromStatus(null);
              setFinalizeReason("");
            }}
            disabled={(requiresFinalizeReason && !finalizeReason.trim()) || (finalizeTarget?.raw?.freightDocumentId && (finalizeStatusLoading || finalizeStatusUnavailable)) || (eDocFinalizationAction === "DECLARE_DELIVERED" && finalizeTarget?.raw?.freightDocumentId && finalizeTarget.raw.edocStatus !== "DELIVERED" && !deliveryConfirmed)}
          >
            {t("transportManagement.confirmFinalizeAction", {
              defaultValue: "Finalize",
            })}
          </Button>,
        ]}
      >
        <Typography.Paragraph>
          {t("transportManagement.confirmFinalizeBody", {
            defaultValue: "This will finalize the transport order. Do you want to continue?",
          })}
        </Typography.Paragraph>
        {Boolean(
          finalizeTarget?.raw?.freightDocumentId ||
            finalizeTarget?.raw?.viewPdfUrl ||
            finalizeTarget?.raw?.generateEDocs ||
            finalizeTarget?.raw?.generateECMR
        ) && (
          <div style={{
            display: "grid",
            gap: 10,
            marginBottom: 12,
            padding: 12,
            border: `1px solid ${eDocFinalizationAction === "CANCEL" ? "#faad14" : "#91caff"}`,
            borderRadius: 8,
            background: eDocFinalizationAction === "CANCEL" ? "#fffbe6" : "#e6f4ff",
          }}>
            <Typography.Text strong>
              {t("transportManagement.eDocFinalizationTitle", {
                defaultValue: "Electronic document",
              })}
            </Typography.Text>
            <Radio.Group
              value={eDocFinalizationAction}
              onChange={(event) => setEDocFinalizationAction(event.target.value)}
              style={{ display: "grid", gap: 8 }}
            >
              <Radio value="DECLARE_DELIVERED">
                {t("transportManagement.declareDelivery")}
              </Radio>
              <Radio value="KEEP_TRANSIT">
                {t("transportManagement.keepEDocInTransit", {
                  defaultValue: "Finalize the order and keep the e-Doc in its current state",
                })}
              </Radio>
              <Radio value="REQUIRE_DELIVERED">
                {t("transportManagement.requireDeliveredEDoc", {
                  defaultValue: "Finalize only after delivery is signed in TransFollow",
                })}
              </Radio>
              <Radio value="CANCEL">
                {t("transportManagement.cancelEDocOnFinalize", {
                  defaultValue: "Cancel e-Doc on finalization",
                })}
              </Radio>
            </Radio.Group>
            {finalizeStatusUnavailable && <Alert type="warning" message={t('feedback.unavailable')} />}
            {eDocFinalizationAction === "DECLARE_DELIVERED" && finalizeTarget?.raw?.edocStatus !== "DELIVERED" && (
              <Checkbox checked={deliveryConfirmed} onChange={event => setDeliveryConfirmed(event.target.checked)}>
                {t("transportManagement.deliveryResponsibility")}
              </Checkbox>
            )}
            {eDocFinalizationAction === "REQUIRE_DELIVERED" && finalizeTarget?.raw?.accessCode ? (
              <Button
                href={`${finalizeTarget.raw.transfollowEnvironment === 'PRODUCTION' ? 'https://portal.transfollow.com' : 'https://portal.partner.transfollow.com'}/sign/signature_paired_sog?carrierAccessCode=${encodeURIComponent(finalizeTarget.raw.accessCode)}`}
                target="_blank"
                rel="noreferrer"
              >
                {t("transportManagement.openTransFollowDelivery", {
                  defaultValue: "Complete delivery signatures in TransFollow",
                })}
              </Button>
            ) : null}
            <Typography.Text type="secondary">
              {t(`transportManagement.eDocFinalizationHelp.${eDocFinalizationAction}`, {
                defaultValue:
                  eDocFinalizationAction === "DECLARE_DELIVERED"
                    ? t("transportManagement.deliveryDeclarationHelp")
                    : eDocFinalizationAction === "REQUIRE_DELIVERED"
                    ? "The order will only be finalized after TransFollow confirms a delivery proof."
                    : eDocFinalizationAction === "CANCEL"
                    ? "The electronic document will be cancelled in TransFollow."
                    : "The Gandolapp order will be finalized without changing the TransFollow document status.",
              })}
            </Typography.Text>
          </div>
        )}
        {requiresFinalizeReason && (
          <Input.TextArea
            rows={4}
            placeholder={t("transportManagement.finalizeReason", {
              defaultValue: "Provide a reason for manual finalization",
            })}
            value={finalizeReason}
            onChange={(e) => setFinalizeReason(e.target.value)}
          />
        )}
      </Modal>
    </>
      )}
    </div>
  );
};

export default AssignmentKanban;
